/** Conversational voice helpers — MediaRecorder only, no browser VAD. */

export const VOICE_STATES = {
  IDLE: 'IDLE',
  LISTENING: 'LISTENING',
  TRANSCRIBING: 'TRANSCRIBING',
  THINKING: 'THINKING',
  SPEAKING: 'SPEAKING',
  INTERRUPTED: 'INTERRUPTED',
  ERROR: 'ERROR',
};

export const voiceStateLabel = (state, detail = '') => {
  switch (state) {
    case VOICE_STATES.LISTENING:
      return 'Listening…';
    case VOICE_STATES.TRANSCRIBING:
      return 'Transcribing your speech…';
    case VOICE_STATES.THINKING:
      return detail || 'Understanding your request…';
    case VOICE_STATES.SPEAKING:
      return detail || 'Speaking…';
    case VOICE_STATES.INTERRUPTED:
      return 'Interrupted';
    case VOICE_STATES.ERROR:
      return detail || 'Voice error';
    default:
      return '';
  }
};

export const createAudioQueuePlayer = () => {
  let currentAudio = null;
  let queue = [];
  let playing = false;
  let stopped = false;
  let onStateChange = null;

  const notify = (isPlaying) => {
    if (onStateChange) onStateChange(isPlaying);
  };

  const stopAll = () => {
    stopped = true;
    queue = [];
    if (currentAudio) {
      try {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      } catch (_) {
        /* ignore */
      }
      currentAudio = null;
    }
    playing = false;
    notify(false);
  };

  const playNext = () => {
    if (stopped) {
      playing = false;
      notify(false);
      return;
    }
    if (!queue.length) {
      playing = false;
      currentAudio = null;
      notify(false);
      return;
    }
    const { url, revoke } = queue.shift();
    const audio = new Audio(url);
    currentAudio = audio;
    playing = true;
    notify(true);
    audio.onended = () => {
      if (revoke) URL.revokeObjectURL(url);
      playNext();
    };
    audio.onerror = () => {
      if (revoke) URL.revokeObjectURL(url);
      playNext();
    };
    audio.play().catch(() => playNext());
  };

  const enqueueBase64Wav = (base64, mimeType = 'audio/wav') => {
    stopped = false;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    queue.push({ url, revoke: true });
    if (!playing) playNext();
  };

  const playBase64WavNow = (base64, mimeType = 'audio/wav') => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    return new Promise((resolve) => {
      const cleanup = () => URL.revokeObjectURL(url);
      audio.onended = () => {
        cleanup();
        resolve();
      };
      audio.onerror = () => {
        cleanup();
        resolve();
      };
      audio.play().catch(() => resolve());
      currentAudio = audio;
    });
  };

  const stopCurrent = () => {
    if (currentAudio) {
      try {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      } catch (_) {
        /* ignore */
      }
      currentAudio = null;
    }
  };

  return {
    enqueueBase64Wav,
    playBase64WavNow,
    stopAll,
    stopCurrent,
    isPlaying: () => playing,
    setOnStateChange: (fn) => {
      onStateChange = fn;
    },
    resetStopped: () => {
      stopped = false;
    },
  };
};

export const base64ToBlobUrl = (base64, mimeType = 'audio/wav') => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: mimeType });
  return URL.createObjectURL(blob);
};

const floatTo16BitPCM = (float32) => {
  const buffer = new ArrayBuffer(float32.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < float32.length; i += 1) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};

/**
 * Tap microphone PCM from the same MediaStream used by MediaRecorder.
 * Chunks are sent to backend Silero VAD (no browser VAD package).
 */
export const startPcmCapture = (stream, { onPcmChunk }) => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioCtx();
  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);

  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    const pcm = floatTo16BitPCM(input);
    onPcmChunk(pcm, audioContext.sampleRate);
  };

  source.connect(processor);
  processor.connect(audioContext.destination);

  return {
    stop: () => {
      try {
        processor.disconnect();
        source.disconnect();
        processor.onaudioprocess = null;
      } catch (_) {
        /* ignore */
      }
      return audioContext.close().catch(() => {});
    },
    getSampleRate: () => audioContext.sampleRate,
  };
};

export const pcmToBase64 = arrayBufferToBase64;

